import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deliveryOrdersAPI,
  getApiErrorMessage,
  type DeliveryOrderRowDto,
  type ListDeliveryOrdersParams,
} from '@matix/api-client';

export type DeliveryFilter = 'unpaid' | 'paid' | 'all';

/**
 * The Delivery workspace's list, polled.
 *
 * Polling rather than the call centre's SSE stream on purpose: that stream is
 * published from `cc_order` writes, and the orders this screen exists for have
 * no `cc_order` row at all — nothing would ever push. A delivery counter is
 * also not a kitchen board; fifteen seconds is well inside the time it takes a
 * driver to come back.
 */
export function useDeliveryOrders(params: ListDeliveryOrdersParams, pollSeconds = 15) {
  const [orders, setOrders] = useState<DeliveryOrderRowDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Serialised so the effect re-runs on a real change rather than on every
  // render's fresh object identity.
  const key = JSON.stringify(params);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  /**
   * Monotonic request id. Every load stamps one and only writes state if it is
   * still the newest — otherwise a slow response for "10" can land after the
   * fast one for "1042" and leave the grid showing results the cashier has
   * already typed past. A `cancelled` flag on the effect is not enough: it
   * only stops loads that have not STARTED, and several are always in flight
   * while someone is typing.
   */
  const seq = useRef(0);

  const load = useCallback(async (quiet = false) => {
    const mine = ++seq.current;
    if (!quiet) setLoading(true);
    try {
      const res = await deliveryOrdersAPI.list(paramsRef.current);
      if (mine !== seq.current) return; // a newer request has overtaken this one
      setOrders(res.data);
      setTotal(res.total);
      setError(null);
    } catch (err) {
      if (mine !== seq.current) return;
      setError(getApiErrorMessage(err));
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const id = window.setInterval(() => void load(true), Math.max(5, pollSeconds) * 1000);
    return () => {
      // Retire anything in flight so a late response cannot write into the
      // next filter's list.
      seq.current++;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, pollSeconds, load]);

  /** Overlay a row this screen has just changed, so the button is not dead
   *  until the next poll lands. */
  const patch = useCallback((row: DeliveryOrderRowDto) => {
    setOrders((prev) => prev.map((o) => (o.id === row.id ? row : o)));
  }, []);

  return { orders, total, loading, error, refresh: () => void load(true), patch };
}

/**
 * Debounce the search box.
 *
 * Without it every keystroke is a round trip AND tears down the poll interval,
 * so a cashier typing a phone number fires ten requests and never completes a
 * poll cycle.
 */
export function useDebounced<T>(value: T, ms = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}
